/**
 * "Share with a new organization" — collects a NOT-yet-connected recipient
 * (Person / Organization + email) and hands it back via onConfirm. It does
 * NOT send anything: the recipient is staged as a card in the Share dialog,
 * and the actual connection/share request (with its notification) only fires
 * when the user clicks Share there.
 *
 * Permission lives on the card in the Share dialog, not here.
 */
import { File, Folder, X } from 'lucide-react'
import { useTheme, getThemeClass } from '@papan/core-ui/ui'
import { ConnectionForm, type ConnectionFormValues } from './ConnectionForm.js'

interface Props {
  folderName: string
  isFolder?: boolean
  folderPath?: string
  /** True when editing an already-staged recipient (vs adding a new one). */
  editing?: boolean
  initialEmail?: string
  initialOrganizationName?: string
  initialIsOrganization?: boolean
  initialSetAsDefaultContact?: boolean
  /** Email-only mode: editing the contact address of an already-known
   *  connection. Hides the target-type + org-name controls (the org is
   *  fixed) and edits just the email + the "set as default contact" toggle. */
  lockOrganization?: boolean
  /** Stage/update this recipient (a card in the Share dialog). No send. */
  onConfirm: (values: ConnectionFormValues) => void
  onClose: () => void
}

export function ShareToNewOrgDialog({ folderName, isFolder = true, folderPath, editing = false, initialEmail, initialOrganizationName, initialIsOrganization = true, initialSetAsDefaultContact = false, lockOrganization = false, onConfirm, onClose }: Props) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center px-4"
      onClick={onClose}
      onKeyDown={e => { if (e.key === 'Escape') onClose() }}
    >
      <div className="fixed inset-0 bg-black/50" />
      <div
        className={`relative z-10 mt-[10vh] rounded-lg shadow-xl max-w-md w-full ${themeClass} themed-bg-secondary`}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <h3 className="text-base font-semibold themed-text">{lockOrganization ? 'Edit contact email' : editing ? 'Edit outsider' : 'Share with a new outsider'}</h3>
          <button onClick={onClose} className="p-1 themed-text-muted hover:themed-text transition-colors rounded" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Shared-node context */}
        <div className="px-5 pb-3">
          <div className="flex items-center gap-2 px-3 py-2 themed-bg-tertiary rounded-md border themed-border">
            {isFolder
              ? <Folder size={16} className="text-yellow-500 shrink-0" />
              : <File size={16} className="text-sky-400 shrink-0" />}
            <div className="min-w-0">
              <div className="themed-text text-sm truncate">{folderName}</div>
              {folderPath && <div className="text-xs themed-text-muted truncate" title={folderPath}>{folderPath}</div>}
            </div>
          </div>
          <p className="text-xs themed-text-muted mt-2">
            They'll be added below. The connection request is sent when you click Share.
          </p>
        </div>

        {/* Recipient — Person / Organization + email. No permission here. */}
        <div className="px-5 pb-5">
          <ConnectionForm
            mode="share"
            submitLabel={editing ? 'Save' : 'Confirm'}
            initialEmail={initialEmail}
            initialOrganizationName={initialOrganizationName}
            initialIsOrganization={initialIsOrganization}
            initialSetAsDefaultContact={initialSetAsDefaultContact}
            lockOrganization={lockOrganization}
            onSubmit={async (values) => { onConfirm(values); onClose() }}
            onCancel={onClose}
          />
        </div>
      </div>
    </div>
  )
}
